import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";

export default function ProfileSettings({ open, onOpenChange }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // Change own password state
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  // Admin change-email state
  const [newEmail, setNewEmail] = useState("");
  const [newAdminPw, setNewAdminPw] = useState("");
  const [swapping, setSwapping] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  const submitAdminEmail = async (e) => {
    e.preventDefault();
    setSwapping(true);
    try {
      const { data } = await api.put("/admin/change-email", { new_email: newEmail, new_password: newAdminPw || undefined });
      toast.success(`${data.admin_email} is now an admin`);
      setNewEmail(""); setNewAdminPw("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update admin email");
    } finally {
      setSwapping(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="profile-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Profile Settings</DialogTitle>
          <div className="text-xs text-muted-foreground font-mono mt-1">
            Signed in as <span className="text-foreground">{user?.email}</span> · {user?.role}
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Change own password */}
          <form onSubmit={submitPassword} className="space-y-3" data-testid="profile-change-password-form">
            <div className="flex items-center gap-2">
              <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
              <div className="overline">Change Password</div>
            </div>
            <div>
              <Label className="text-xs">Current password</Label>
              <Input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                className="rounded-none mt-1 h-10"
                data-testid="profile-current-password"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">New password</Label>
                <Input
                  type="password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  required
                  minLength={6}
                  className="rounded-none mt-1 h-10"
                  data-testid="profile-new-password"
                />
              </div>
              <div>
                <Label className="text-xs">Confirm</Label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  className="rounded-none mt-1 h-10"
                  data-testid="profile-confirm-password"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={saving} className="rounded-none" data-testid="profile-change-password-submit">
                {saving ? "Saving…" : "Update Password"}
              </Button>
            </div>
          </form>

          {/* Admin-only: promote another email to admin */}
          {isAdmin && (
            <form onSubmit={submitAdminEmail} className="space-y-3 border-t border-border pt-6" data-testid="admin-change-email-form">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                <div className="overline">Promote / Change Admin Email</div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Promotes the email to the admin role. If it already exists, its role is updated. If not, provide a temporary password to create the account.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">New admin email</Label>
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                    placeholder="new-admin@ask.com"
                    className="rounded-none mt-1 h-10"
                    data-testid="admin-new-email"
                  />
                </div>
                <div>
                  <Label className="text-xs">Temp password (if new)</Label>
                  <Input
                    type="password"
                    value={newAdminPw}
                    onChange={(e) => setNewAdminPw(e.target.value)}
                    placeholder="Only required for a brand-new account"
                    minLength={6}
                    className="rounded-none mt-1 h-10"
                    data-testid="admin-new-password"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={swapping} variant="outline" className="rounded-none" data-testid="admin-change-email-submit">
                  {swapping ? "Applying…" : "Promote to Admin"}
                </Button>
              </div>
            </form>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-none">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
